// Shared plumbing for capturing a photo with a live in-page camera
// (getUserMedia + canvas) instead of handing off to the device's native
// camera app via <input capture>. See GuidedVehicleDamagePhotos.tsx for why
// that hand-off matters: on an iOS device with DMS installed to the home
// screen, it can silently fail to hand the photo back to the page at all.
// Used by every photo field in the incident report that needs a camera, so
// this logic (and its iOS quirks) lives in exactly one place.
import { useCallback, useRef, useState } from "react";

export function useLiveCameraCapture() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  // True once a live preview has failed to start -- the caller should show
  // a native <input capture> fallback instead of a Capture button.
  const [fallback, setFallback] = useState(false);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  // iOS in particular can report the stream as "playing" a beat before any
  // frame has actually decoded, which would otherwise capture a blank canvas.
  const waitForDimensions = (video: HTMLVideoElement, timeoutMs = 3000): Promise<boolean> => {
    const start = performance.now();
    return new Promise((resolve) => {
      const tick = () => {
        if ((video.videoWidth || 0) > 0 && (video.videoHeight || 0) > 0) return resolve(true);
        if (performance.now() - start > timeoutMs) return resolve(false);
        requestAnimationFrame(tick);
      };
      tick();
    });
  };

  const start = useCallback(async () => {
    setError("");
    setFallback(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera not supported in this browser.");
      stop();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error("Video element not ready.");
      video.setAttribute("playsinline", "true");
      video.muted = true;
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        // iOS sometimes needs a user gesture beat -- the dimensions check below covers it.
      }
      const ok = await waitForDimensions(video);
      if (!ok) {
        setFallback(true);
        setError("Live camera preview isn't available on this device -- use your phone's camera instead.");
        stop();
        return;
      }
      setReady(true);
    } catch (e) {
      setFallback(true);
      setError(e instanceof Error ? e.message : "Camera permission was denied or isn't available.");
      stop();
    }
  }, [stop]);

  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video) return null;
    const w = video.videoWidth || 0;
    const h = video.videoHeight || 0;
    if (w === 0 || h === 0) {
      setFallback(true);
      setError("Camera preview isn't ready -- use your phone's camera instead.");
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setError("Couldn't access canvas.");
      return null;
    }
    ctx.drawImage(video, 0, 0, w, h);
    return new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9);
    });
  }, []);

  return { videoRef, ready, error, fallback, start, stop, captureFrame, setError };
}
